import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import {
  getDatabase,
  ref,
  query,
  orderByKey,
  startAt,
  limitToFirst,
  onValue,
  off,
  push,
  update,
} from "firebase/database";

const firebaseConfig = {
  apiKey: "AIzaSyAOwLWRlBri5hOlvGec4BSlFJ8oYZIU-BY",
  authDomain: "orchid11medspa-6e432.firebaseapp.com",
  databaseURL: "https://orchid11medspa-6e432-default-rtdb.firebaseio.com",
  projectId: "orchid11medspa-6e432",
  storageBucket: "orchid11medspa-6e432.appspot.com",
  messagingSenderId: "711778663448",
  appId: "1:711778663448:web:c5455bbf732bbdcdb4176d",
  measurementId: "G-EJ5VSCZD9F",
};

class Firebase {
  constructor() {
    const app = initializeApp(firebaseConfig);
    this.auth = getAuth(app);
    this.db = getDatabase(app);
  }

  /** REALTIME DATABASE API */

  formatterHelperFunction = (year, month) => {
    if (month < 10) {
      const newYear = year * 10;
      return `${newYear}${month}`;
    }
    return `${year}${month}`;
  };

  /**
   * Subscribes to the free-appointments bucket starting at the given
   * year/month key, for `monthFetchingOffset` month buckets.
   * Returns an unsubscribe function (call it on cleanup).
   */
  subscribeToFreeAppointments = (year, month, monthFetchingOffset, callback) => {
    const startKey = this.formatterHelperFunction(year, month);
    const freeAppointmentsRef = ref(this.db, "appointments/freeAppointments");
    const appointmentsQuery = query(
      freeAppointmentsRef,
      orderByKey(),
      startAt(startKey),
      limitToFirst(monthFetchingOffset)
    );
    return onValue(appointmentsQuery, callback);
  };

  /** Generic listener helpers — the admin screens read whole buckets rather
   *  than the paged query above. onValue returns its own unsubscribe; prefer
   *  that over doOff, which drops *every* listener on the path. */
  doOnValue = (reference, callback) => onValue(reference, callback);

  doOff = (reference) => off(reference);

  freeAppointments = () => ref(this.db, "appointments/freeAppointments");

  bookedAppointments = () => ref(this.db, "appointments/bookedAppointments");

  /**
   * Writes every generated slot in one atomic multi-path update, each into the
   * month bucket its start date falls in. The full path is stored back on the
   * slot as `eventKey`, which is what deleting one later targets.
   */
  writeEventData = (events) => {
    const updates = {};
    for (const event of events) {
      const thisDate = new Date(event.start);
      const value = this.formatterHelperFunction(thisDate.getFullYear(), thisDate.getMonth());
      const listRef = ref(this.db, `appointments/freeAppointments/${value}`);
      const newPostKey = push(listRef).key;
      if (newPostKey) {
        const fullPath = `appointments/freeAppointments/${value}/${newPostKey}`;
        updates[fullPath] = { ...event, eventKey: fullPath };
      }
    }
    return update(ref(this.db), updates);
  };

  /** Takes anything carrying an `eventKey`; clears them all in one write. */
  deleteEventsForSure = (events) => {
    const updates = {};
    for (const event of events) {
      updates[event.eventKey] = null;
    }
    return update(ref(this.db), updates);
  };

  /** Cancels a booking: the booked record, and the slot it consumed. */
  deleteBookedAppointment = (userdata) => {
    const updates = {};
    updates[userdata.eventKey] = null;
    updates[userdata.bookedAppointmentKey] = null;
    return update(ref(this.db), updates);
  };

  /** EMPLOYEES API */

  employeesRef = () => ref(this.db, "employees");

  /**
   * Live team list, as an array with the node key kept as `id` — that id is
   * the only thing stored on a slot, so names and colours stay editable.
   * Returns onValue's unsubscribe.
   */
  onEmployeesUpdate = (callback) => {
    return this.doOnValue(this.employeesRef(), (snapshot) => {
      const data = snapshot.val();
      const employeesArray = [];
      if (data) {
        Object.keys(data).forEach((key) => {
          employeesArray.push({ id: key, ...data[key] });
        });
      }
      callback(employeesArray);
    });
  };

  addEmployee = (employee) => {
    const newEmployeeKey = push(this.employeesRef()).key;
    if (!newEmployeeKey) {
      return Promise.reject(new Error("Could not generate a key for the new employee."));
    }
    const updates = {};
    updates[`employees/${newEmployeeKey}`] = {
      name: employee.name,
      color: employee.color,
      role: employee.role || "Professional",
    };
    return update(ref(this.db), updates);
  };

  updateEmployee = (id, updatedData) => {
    const updates = {};
    updates[`employees/${id}`] = {
      name: updatedData.name,
      color: updatedData.color,
      role: updatedData.role || "Professional",
    };
    return update(ref(this.db), updates);
  };

  deleteEmployee = (id) => {
    const updates = {};
    updates[`employees/${id}`] = null;
    return update(ref(this.db), updates);
  };

  /** AUTH API */

  onAuthStateChanged = (callback) => onAuthStateChanged(this.auth, callback);

  doSignInWithEmailAndPassword = (email, password) =>
    signInWithEmailAndPassword(this.auth, email, password);

  doSignOut = () => signOut(this.auth);
}

export default Firebase;
